// @ts-nocheck
/**
 * Create User — 5-step wizard, modeled on obd3-gui v1's CreateUserPage.
 *
 * Steps:
 *   0 Identity & Role     — username, name, role (creator-gated options)
 *   1 Email & Verification — email + OTP flow (conditional)
 *   2 Credentials          — password, confirmPassword
 *   3 Access & Assignment  — ACL settings, account type, company / call center,
 *                            expiryDate (capped by the company / creator expiry)
 *   5 Review               — summary + submit
 *
 * State model: react-hook-form + zod (see schema.ts). Data hooks from
 * api.ts (register, existing usernames, email OTP) + lookups/api.ts
 * (ACL, company, call center, demo settings). Cross-field validation
 * baked into the schema. Per-step `Next` triggers only that step's fields.
 *
 * All submit-payload assembly is byte-identical to v1 UserAddPage's
 * `submitForm`, per the semantic inventory.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { toDateInputString, todayInputString, minDateStr, formatExpiryForDisplay } from '@/utilities/expiryDate';
import { blockManualDateEntry, blockDatePaste } from '@/utilities/dateInputHelpers';
import {
  ArrowLeft,
  ArrowRight,
  Building,
  Check,
  ClipboardCheck,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Stepper, useStepper } from '@/components/ui/stepper';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils/cn';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { AclDenied } from '@/components/feedback/AclDenied';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import {
  AvailabilityHint,
  useUsernameAvailability,
  useUserEmailAvailability,
} from '@/hooks/useAvailability';
import { getUsernameValidationError, validateEmail } from '@/utilities/userCreation';
import { getUser } from '@/utilities/setting';
import { creatorRequiresEmailVerificationOnUserForms } from '@/utilities/userEmailVerificationAcl';
import { isCallCentreUserRole, parseAgentLeadsInput, currentUserHasCustomAgentLeads } from '@/utilities/agentLeadsQuota';
import { USER_ROLES, USERS_ROLE, ROUTES, MODULE_ACL } from '@/utilities/constant';
import { useAuth } from '@/views/dashboard/auth/auth-utils';
import {
  createUserSchema,
  STEP_FIELDS,
  isAclRequired,
  isCallCentreRequired,
  isCompanyRequired,
  type CreateUserInput,
} from './schema';
import { useExistingUsernames, useRegisterUser, useUserProfile } from './api';
import {
  useAclSettings,
  useCallCentreList,
  useCompanyList,
  useDemoAccountSettings,
} from './lookups/api';
import { RolePicker } from './components/RolePicker';
import { EmailVerifier } from './components/EmailVerifier';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import { DRIVE_LINK_ROLES, normalizeDriveFrequencies } from '@/utilities/googleDriveAcl';
import { generateRandomPassword } from '@/utilities/userCreation';

const STEPS = [
  { id: 'account', label: 'Account', description: 'Identity, email, credentials', icon: UserRound },
  { id: 'access', label: 'Access', description: 'ACL & assignment', icon: Building },
  { id: 'review', label: 'Review', description: 'Confirm & create', icon: ClipboardCheck },
] as const;

export default function UserAddPage() {
  const navigate = useNavigate();
  useAuth();
  const { canAdd } = useModuleAcl(MODULE_ACL.USERS);
  const stepper = useStepper(STEPS.length);
  const [showPassword, setShowPassword] = useState(true);
  const [initialPassword] = useState(generateRandomPassword);
  const [showConfirm, setShowConfirm] = useState(false);
  const [emailVerifiedToken, setEmailVerifiedToken] = useState<string>('');
  const [emailVerified, setEmailVerified] = useState(false);

  const creator = useMemo(() => getUser() || {}, []);
  const canConfigureLeads = useMemo(() => currentUserHasCustomAgentLeads(creator), [creator]);
  const creatorRole = creator?.role || '';
  const creatorId = creator?.id ?? null;

  const isSuperAdmin =
    creatorRole === USER_ROLES.SUPERADMIN || creatorRole === USER_ROLES.SUB_SUPERADMIN;
  const isReseller = creatorRole === USER_ROLES.RESELLER;
  const isCompanyAdmin = creatorRole === USER_ROLES.COMPANY_ADMIN;
  const isCallCentreAdmin = creatorRole === USER_ROLES.CALL_CENTRE_ADMIN;

  // Creator's own-company + chain flags (mirrors v1 gating). Best-effort
  // reads from the stored user; empty defaults fall back to permissive mode.
  const assignedCompany = creator?.assignedCompany || creator?.companies || null;
  const isOwnCompany = Boolean(assignedCompany?.is_own_company);
  const isAllCompanyAdminChain = Boolean(creator?.chain_all_company_admin);
  const isAllResellerChain = Boolean(creator?.chain_all_reseller);

  /* ── Data hooks ─────────────────────────────────────────────── */
  const existingUsernames = useExistingUsernames();
  const parentProfile = useUserProfile(isSuperAdmin ? null : creatorId);
  const demoSettings = useDemoAccountSettings(Boolean(assignedCompany?.is_demo_company));
  const companyList = useCompanyList(!(isCompanyAdmin && isOwnCompany));
  const callCentreList = useCallCentreList(!isCallCentreAdmin);
  const register = useRegisterUser();

  const parentAccountType =
    parentProfile.data?.account_type ?? (creator?.account_type as number | undefined);

  const defaultExpiry = useMemo(() => {
    const days = Number(demoSettings.data?.expiry_days_per_recharge);
    if (Number.isFinite(days) && days > 0) {
      const d = new Date();
      d.setDate(d.getDate() + days - 1);
      return d.toISOString().slice(0, 10);
    }
    return '';
  }, [demoSettings.data]);

  /* ── Form ───────────────────────────────────────────────────── */
  const form = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    mode: 'onTouched',
    defaultValues: {
      username: '',
      name: '',
      role: '',
      email: '',
      signupEmailToken: '',
      password: initialPassword,
      confirmPassword: initialPassword,
      expiryDate: '',
      aclSettings: '',
      accountType: isSuperAdmin ? '0' : String(parentAccountType ?? '0'),
      company: '',
      callCentreId: '',
      agentLeads: '100',
    },
  });

  // Once demo defaults resolve, patch the expiry (only if user hasn't touched it).
  useEffect(() => {
    if (defaultExpiry && !form.getValues('expiryDate')) {
      form.setValue('expiryDate', defaultExpiry, { shouldDirty: false });
    }
  }, [defaultExpiry, form]);

  // Prefill account type once parent profile hydrates.
  useEffect(() => {
    if (!isSuperAdmin && parentAccountType !== undefined) {
      form.setValue('accountType', String(parentAccountType), { shouldDirty: false });
    }
  }, [isSuperAdmin, parentAccountType, form]);

  // Company preselect for own-company creators.
  useEffect(() => {
    if (isOwnCompany && (isCompanyAdmin || isReseller) && assignedCompany?.id) {
      form.setValue('company', String(assignedCompany.id), { shouldDirty: false });
    }
  }, [isOwnCompany, isCompanyAdmin, isReseller, assignedCompany, form]);

  const role = form.watch('role');
  const email = form.watch('email');
  const username = form.watch('username');

  const emailVerificationRequired = useMemo(
    () =>
      creatorRequiresEmailVerificationOnUserForms(creator, { isSuperAdmin, userRole: role }),
    [creator, isSuperAdmin, role],
  );

  // ACL list keyed on target role — auto-select the default match.
  const aclList = useAclSettings(isAclRequired(role) ? role : null);
  const aclSettingsId = form.watch('aclSettings');
  const driveFrequencies = useMemo(() => {
    const row = (aclList.data ?? []).find((item) => String(item.id) === String(aclSettingsId));
    return normalizeDriveFrequencies(row?.drive_frequencies);
  }, [aclList.data, aclSettingsId]);
  const driveOffered = DRIVE_LINK_ROLES.includes(role) && driveFrequencies.length > 0;

  useEffect(() => {
    if (!aclList.data?.length) return;
    if (form.getValues('aclSettings')) return;
    const def = aclList.data.find((a) => a.default_for_role === role);
    if (def) form.setValue('aclSettings', String(def.id), { shouldDirty: false });
  }, [aclList.data, role, form]);

  // Filter company list for SUPERADMIN (only companies they created).
  const filteredCompanies = useMemo(() => {
    if (!companyList.data) return [];
    if (isSuperAdmin) return companyList.data.filter((c) => c.created_by == creatorId);
    return companyList.data;
  }, [companyList.data, isSuperAdmin, creatorId]);
  const selectedCompanyId = form.watch('company');
  const selectedCompanyExpiry = useMemo(() => {
    const comp = (companyList.data ?? []).find((c) => String(c.id) === String(selectedCompanyId));
    return comp?.expiry_date || null;
  }, [companyList.data, selectedCompanyId]);

  const companyExpiryStr = toDateInputString(selectedCompanyExpiry);
  const parentExpiryStr = minDateStr(
    parentProfile.data?.expiry_date,
    creator?.expiry_date,
    parentProfile.data?.assignedCompany?.expiry_date,
    creator?.assignedCompany?.expiry_date,
    creator?.assigned_company_details?.expiry_date,
  );
  const effectiveExpiryMax = minDateStr(companyExpiryStr, parentExpiryStr);
  const todayStr = todayInputString();
  const expiryCapSource =
    effectiveExpiryMax && effectiveExpiryMax === companyExpiryStr
      ? 'the selected company'
      : 'your own account';

  const expiryValue = form.watch('expiryDate');
  const expiryWithinCap =
    !effectiveExpiryMax || !expiryValue || expiryValue <= effectiveExpiryMax;
  const expiryCapMessage = `Cannot be later than ${formatExpiryForDisplay(effectiveExpiryMax)} — the expiry of ${expiryCapSource}`;

  useEffect(() => {
    if (!effectiveExpiryMax) return;
    const current = form.getValues('expiryDate');
    if (!current) {
      form.setValue('expiryDate', effectiveExpiryMax, { shouldDirty: false });
    } else if (current > effectiveExpiryMax) {
      form.setValue('expiryDate', effectiveExpiryMax, { shouldDirty: false });
    }
  }, [effectiveExpiryMax, form]);

  /* ── Inline username uniqueness feedback ────────────────────── */
  const usernameStatus = useUsernameAvailability(username ?? '', {
    enabled: !getUsernameValidationError(username ?? ''),
  });
  const emailStatus = useUserEmailAvailability(email ?? '', {
    enabled: !validateEmail(email ?? ''),
  });
  const emailTaken = emailStatus === 'taken';
  const usernameTaken = useMemo(() => {
    const u = username?.trim().toLowerCase();
    if (!u) return false;
    return Boolean(existingUsernames.data?.includes(u)) || usernameStatus === 'taken';
  }, [username, existingUsernames.data, usernameStatus]);

  /* ── Step navigation ────────────────────────────────────────── */
  const handleNext = async () => {
    const fields = STEP_FIELDS[stepper.current] ?? [];
    const ok = fields.length ? await form.trigger(fields) : true;
    if (!ok) return;

    if (stepper.current === 1 && !expiryWithinCap) {
      form.setError('expiryDate', { message: expiryCapMessage });
      return;
    }

    // Step 0 now bundles identity + email + credentials — enforce both
    // uniqueness and OTP-gate before moving on.
    if (stepper.current === 0) {
      if (usernameTaken) {
        form.setError('username', { message: 'Username already taken' });
        return;
      }
      if (emailTaken) {
        form.setError('email', { message: 'Email already exists' });
        return;
      }
      if (emailVerificationRequired && !emailVerified) {
        toast.error('Verify the email address before continuing');
        return;
      }
    }
    stepper.next();
  };

  /* ── Submit ─────────────────────────────────────────────────── */
  const onSubmit = async (input: CreateUserInput) => {
    if (stepper.current !== STEPS.length - 1) return;
    if (!expiryWithinCap) {
      form.setError('expiryDate', { message: expiryCapMessage });
      stepper.goTo(1);
      return;
    }
    if (usernameTaken) {
      form.setError('username', { message: 'Username already taken' });
      stepper.goTo(0);
      return;
    }
    if (emailTaken) {
      form.setError('email', { message: 'Email already exists' });
      stepper.goTo(0);
      return;
    }
    if (emailVerificationRequired && !emailVerified) {
      toast.error('Email must be verified before submitting');
      stepper.goTo(0);
      return;
    }
    try {
      const payload: any = {
        username: input.username.trim(),
        password: input.password,
        name: input.name.trim(),
        email: input.email.trim(),
        signup_email_token: emailVerifiedToken || undefined,
        acl_settings: input.aclSettings || null,
        role: input.role,
        expiry_date: input.expiryDate,
        account_type: Number(input.accountType),
      };

      if (isCompanyRequired(input.role)) payload.company = input.company || null;
      if (isCallCentreRequired(input.role)) payload.callcenterId = input.callCentreId;
      if (canConfigureLeads && isCallCentreUserRole(input.role)) {
        const agentLeads = parseAgentLeadsInput(input.agentLeads);
        payload.agent_leads = agentLeads.ok ? agentLeads.value : 100;
      }

      await register.mutateAsync(payload);
      toast.success('User created');
      setTimeout(() => navigate(`/${ROUTES.USERS.LIST}`), 1200);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || 'Could not create the user');
    }
  };

  const completedSteps = useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < stepper.current; i += 1) arr.push(i);
    return arr;
  }, [stepper.current]);

  if (!canAdd) return <AclDenied operation="add" />;

  return (
    <ScrollPage>
      <PageHeader
        title="Add user"
        description="Create a new user under your account."
        actions={
          <Button variant="ghost" onClick={() => navigate(`/${ROUTES.USERS.LIST}`)}>
            Cancel
          </Button>
        }
      />

      <Stepper
        steps={STEPS as unknown as any[]}
        current={stepper.current}
        completed={completedSteps}
        onStepClick={(i) => (i < stepper.current ? stepper.goTo(i) : undefined)}
      />

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="mt-6 space-y-6"
          noValidate
        >
          {/* ── Step 0: Account (identity + email + credentials) ── */}
          {stepper.current === 0 && (
            <div className="space-y-4">
              {/* Identity / Role */}
              <Card>
                <CardHeader>
                  <CardTitle>Identity</CardTitle>
                  <CardDescription>
                    Login name, display name, and the role this user will operate as.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="username"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Username *</FormLabel>
                        <FormControl>
                          <Input {...field} maxLength={50} placeholder="e.g. rahul_kumar" />
                        </FormControl>
                        {!form.formState.errors.username && usernameTaken ? (
                          <p role="alert" className="text-xs text-destructive">
                            Username already taken
                          </p>
                        ) : (
                          !form.formState.errors.username && (
                            <AvailabilityHint
                              status={usernameStatus}
                              takenMessage="Username already taken"
                            />
                          )
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full name *</FormLabel>
                        <FormControl>
                          <Input {...field} maxLength={100} placeholder="e.g. Rahul Kumar" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Role *</FormLabel>
                      <FormControl>
                        <RolePicker
                          value={field.value}
                          onChange={field.onChange}
                          creatorRole={creatorRole}
                          isOwnCompany={isOwnCompany}
                          isAllCompanyAdminChain={isAllCompanyAdminChain}
                          isAllResellerChain={isAllResellerChain}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                </CardContent>
              </Card>

              {/* Email & Verification */}
              <Card>
                <CardHeader>
                  <CardTitle>Email</CardTitle>
                  <CardDescription>
                    Contact address for this user; may require verification before submit.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <EmailVerifier
                          email={field.value}
                          onEmailChange={(v) => field.onChange(v)}
                          onReset={() => {
                            setEmailVerified(false);
                            setEmailVerifiedToken('');
                            form.setValue('signupEmailToken', '');
                          }}
                          onVerified={(token) => {
                            setEmailVerified(true);
                            setEmailVerifiedToken(token);
                            form.setValue('signupEmailToken', token);
                          }}
                          required={emailVerificationRequired}
                          verified={emailVerified}
                          emailError={form.formState.errors.email?.message as string | undefined}
                        />
                      </FormControl>
                      {!form.formState.errors.email && (
                        <AvailabilityHint status={emailStatus} takenMessage="Email already exists" />
                      )}
                      {/* EmailVerifier already renders emailError inline;
                          FormMessage would duplicate it. */}
                    </FormItem>
                  )}
                />
                {emailVerificationRequired && (
                  <p className="text-xs text-muted-foreground">
                    This creator role requires the new user's email to be verified before submission.
                  </p>
                )}
                </CardContent>
              </Card>

              {/* Credentials & Expiry */}
              <Card>
                <CardHeader>
                  <CardTitle>Credentials</CardTitle>
                  <CardDescription>
                    Initial password for this account.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              type={showPassword ? 'text' : 'password'}
                              maxLength={100}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((v) => !v)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                              aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm password *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              {...field}
                              type={showConfirm ? 'text' : 'password'}
                              maxLength={100}
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirm((v) => !v)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                              aria-label={showConfirm ? 'Hide password' : 'Show password'}
                            >
                              {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* ── Step 1: Access & Assignment ──────────────────────── */}
          {stepper.current === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Access &amp; Assignment</CardTitle>
                <CardDescription>
                  ACL, account type, and organisation this user belongs to.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
              {isAclRequired(role) && (
                <FormField
                  control={form.control}
                  name="aclSettings"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ACL setting *</FormLabel>
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={aclList.isLoading ? 'Loading…' : 'Pick an ACL setting'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(aclList.data ?? []).map((a) => (
                            <SelectItem key={a.id} value={String(a.id)}>
                              {a.acl_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="accountType"
                render={({ field }) => {
                  const accountOptions = [
                    {
                      value: '0',
                      label: 'Promotional',
                      hint: 'Marketing / bulk-outreach traffic. DND-honouring.',
                    },
                    {
                      value: '1',
                      label: 'Transactional',
                      hint: 'Alerts, OTPs, service notifications. Delivers to all numbers.',
                    },
                  ];
                  const disabled = !isSuperAdmin;
                  const val = field.value ?? '0';
                  return (
                    <FormItem>
                      <FormLabel>Account type *</FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={val}
                          onValueChange={field.onChange}
                          disabled={disabled}
                          className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                        >
                          {accountOptions.map((opt) => {
                            const selected = val === opt.value;
                            return (
                              <label
                                key={opt.value}
                                htmlFor={`acct-${opt.value}`}
                                className={cn(
                                  'flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 transition-colors',
                                  selected ? 'border-primary ring-1 ring-primary/40' : 'hover:border-ring',
                                  disabled && 'cursor-not-allowed opacity-60',
                                )}
                              >
                                <RadioGroupItem
                                  value={opt.value}
                                  id={`acct-${opt.value}`}
                                  className="mt-0.5"
                                />
                                <div className="min-w-0">
                                  <div className="text-sm font-medium">{opt.label}</div>
                                  <div className="mt-0.5 text-xs text-muted-foreground">
                                    {opt.hint}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        {creatorRole === USER_ROLES.SUPERADMIN
                          ? 'You can change this later. A change applies to every user under this account.'
                          : 'This follows your account type.'}
                      </p>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              {isCompanyRequired(role) && (
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company *</FormLabel>
                      <Select
                        value={field.value ?? ''}
                        onValueChange={field.onChange}
                        disabled={isOwnCompany && isCompanyAdmin}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={companyList.isLoading ? 'Loading…' : 'Pick a company'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {filteredCompanies.map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {canConfigureLeads && isCallCentreUserRole(role) && (
                <FormField
                  control={form.control}
                  name="agentLeads"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Agent leads *</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} step={1} {...field} />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Stored on this call centre user. Defaults to 100.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isCallCentreRequired(role) && (
                <FormField
                  control={form.control}
                  name="callCentreId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Call centre *</FormLabel>
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={callCentreList.isLoading ? 'Loading…' : 'Pick a call center'} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(callCentreList.data ?? []).map((c) => (
                            <SelectItem key={c.id} value={String(c.id)}>
                              {c.callcentre_name || c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="expiryDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiry date *</FormLabel>
                    <FormControl>
                      <CalendarDateInput
                        value={field.value}
                        onChange={(e) => {
                          const value = e;
                          if (effectiveExpiryMax && value && value > effectiveExpiryMax) {
                            toast.error(
                              `Expiry cannot be later than ${formatExpiryForDisplay(effectiveExpiryMax)} — the expiry of ${expiryCapSource}.`,
                            );
                            field.onChange(effectiveExpiryMax);
                            return;
                          }
                          field.onChange(value);
                        }}
                        min={todayStr}
                        max={effectiveExpiryMax || undefined}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      {effectiveExpiryMax
                        ? `This user cannot expire later than ${formatExpiryForDisplay(effectiveExpiryMax)}, the expiry of ${expiryCapSource}. Anything they create later is capped at the date you set here.`
                        : isCompanyRequired(role)
                          ? 'Pick a company first — its expiry sets the latest date you can choose here.'
                          : 'The expiry date cannot be later than your own account expiry.'}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
              </CardContent>
            </Card>
          )}

          {/* ── Step 2: Review ──────────────────────────────────── */}
          {stepper.current === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>Confirm the details</CardTitle>
                <CardDescription>
                  Review the values you entered above before creating this user.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ReviewGrid values={form.getValues()} role={role} showAgentLeads={canConfigureLeads} />
                {driveOffered && (
                  <p className="mt-4 text-sm text-muted-foreground">
                    Google Drive is not linked here. After this user signs in, they connect their own Google account from their profile.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* ── Nav ─────────────────────────────────────────────── */}
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={stepper.prev}
              disabled={stepper.current === 0}
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            {stepper.current < STEPS.length - 1 ? (
              <Button key="wizard-next" type="button" onClick={handleNext}>
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                key="wizard-submit"
                type="submit"
                disabled={register.isPending}
              >
                <Check className="h-4 w-4" />
                {register.isPending ? 'Creating…' : 'Create user'}
              </Button>
            )}
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

function ReviewGrid({ values, role, showAgentLeads }: any) {
  const roleLabel = USERS_ROLE[values.role] || values.role || '—';
  const acctLabel = values.accountType === '1' ? 'Transactional' : 'Promotional';
  const rows: Array<[string, any]> = [
    ['Username', values.username],
    ['Full name', values.name],
    ['Role', roleLabel],
    ['Email', values.email],
    ['Expiry', values.expiryDate],
    ['Account type', acctLabel],
    ['ACL setting', values.aclSettings || '—'],
    ['Company', values.company || '—'],
    ['Call centre', values.callCentreId || '—'],
    ...(showAgentLeads && isCallCentreUserRole(values.role) ? [['Agent leads', values.agentLeads || '100']] : []),
  ];
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {rows.map(([label, val]) => (
        <div key={label} className="rounded-md border bg-background/60 px-3 py-2">
          <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </dt>
          <dd className="mt-0.5 truncate text-sm font-medium">{String(val ?? '—')}</dd>
        </div>
      ))}
    </dl>
  );
}
